import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowRight, ClipboardList, LoaderCircle, UserRoundX } from 'lucide-react'

import { JobAssignCrewControls } from '@/components/jobs/job-assign-crew-controls'
import { assignmentsApi } from '@/services/assignments'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { JobTitleLink } from '@/components/jobs/job-title-link'
import { groupAssignmentsByJob } from '@/lib/crew-assignment'
import { formatAssignmentScheduleForLocale, useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { withServiceInstanceId } from '@/lib/service-instance'
import type { Assignment } from '@/types'

interface UnassignedAssignmentsAttentionProps {
  count?: number
  isLoading?: boolean
  serviceInstanceId?: string | null
  className?: string
}

const getRoleLabel = (assignment: Assignment) =>
  assignment.role_label || assignment.planner_resource_label || 'Role not specified'

export function UnassignedAssignmentsAttention({
  count = 0,
  isLoading = false,
  serviceInstanceId,
  className = '',
}: UnassignedAssignmentsAttentionProps) {
  const queryClient = useQueryClient()
  const { locale: orgLocale, timeZone: orgTimeZone } = useOrganizationDateTimeFormat(serviceInstanceId)
  const [open, setOpen] = useState(false)
  const resolvedCount = Number.isFinite(count) ? count : 0

  const { data, isLoading: listLoading, isFetching } = useQuery({
    queryKey: ['unassigned-assignments', serviceInstanceId],
    queryFn: () => assignmentsApi.getAssignments({ unassigned: true, page_size: 100 }),
    enabled: open,
    staleTime: 30_000,
  })

  const assignments = data?.results ?? []
  const listCount = data?.count ?? resolvedCount
  const groupedByJob = useMemo(() => groupAssignmentsByJob(assignments), [assignments])

  const refreshUnassigned = async () => {
    await queryClient.invalidateQueries({ queryKey: ['unassigned-assignments', serviceInstanceId] })
    await queryClient.invalidateQueries({ queryKey: ['unassigned-assignments-count', serviceInstanceId] })
  }

  if (isLoading) {
    return <Skeleton className={`h-8 w-44 rounded-full ${className}`} />
  }

  if (resolvedCount <= 0) {
    return null
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 ${className}`}
      >
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {resolvedCount} unassigned assignment{resolvedCount === 1 ? '' : 's'}
        <ArrowRight className="h-3 w-3 shrink-0 opacity-70" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-hidden p-0">
          <DialogHeader className="border-b border-border/70 px-5 py-4">
            <DialogTitle className="flex items-center gap-2 text-base">
              <UserRoundX className="h-4 w-4 text-amber-700" />
              Unassigned job assignments
            </DialogTitle>
            <DialogDescription>
              Active assignment slots with no worker allocated yet. Team jobs can be filled in one action.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
            {listLoading || isFetching ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            ) : assignments.length === 0 ? (
              <div className="flex min-h-28 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                <ClipboardList className="h-8 w-8 opacity-60" />
                <div>
                  <p className="text-sm font-medium text-foreground">No unassigned assignments</p>
                  <p className="mt-0.5 text-xs">All active slots currently have workers assigned.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Showing {assignments.length} of {listCount} open slot{listCount === 1 ? '' : 's'} across {groupedByJob.length} job{groupedByJob.length === 1 ? '' : 's'}.
                </p>
                {groupedByJob.map(([jobId, jobAssignments]) => {
                  const sample = jobAssignments[0]
                  return (
                    <div key={jobId} className="space-y-2 rounded-xl border border-border/70 p-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <JobTitleLink
                          jobId={jobId}
                          title={sample?.job_title}
                          jobRef={sample?.job_ref}
                          serviceInstanceId={serviceInstanceId}
                          className="text-sm font-medium text-foreground hover:text-primary"
                          onClick={() => setOpen(false)}
                        />
                        {sample?.job_ref ? (
                          <Badge variant="outline" className="px-1 py-0 text-[10px]">
                            {sample.job_ref}
                          </Badge>
                        ) : null}
                        <Badge variant="secondary" className="px-1 py-0 text-[10px]">
                          {jobAssignments.length} open slot{jobAssignments.length === 1 ? '' : 's'}
                        </Badge>
                      </div>

                      <JobAssignCrewControls
                        jobId={jobId}
                        assignments={jobAssignments}
                        serviceInstanceId={serviceInstanceId}
                        variant="compact"
                        onAssigned={refreshUnassigned}
                      />

                      <div className="space-y-2">
                        {jobAssignments.map((assignment) => (
                          <div key={assignment.id} className="rounded-lg border border-border/70 bg-background px-3 py-2.5">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">{getRoleLabel(assignment)}</p>
                                <p className="mt-0.5 text-[11px] text-muted-foreground">
                                  {formatAssignmentScheduleForLocale(assignment, orgLocale, 'No assignment schedule', orgTimeZone)}
                                  {assignment.requirement_slot ? ` • Slot ${assignment.requirement_slot}` : ''}
                                  {assignment.crew_template_slot ? ' • Team slot' : ''}
                                </p>
                              </div>
                              <Button variant="outline" size="sm" className="h-7 shrink-0 rounded-md text-xs" asChild>
                                <Link
                                  to={withServiceInstanceId(`/assignments/${assignment.id}`, serviceInstanceId)}
                                  onClick={() => setOpen(false)}
                                >
                                  Open
                                </Link>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border/70 px-5 py-3">
            <p className="text-xs text-muted-foreground">
              {listLoading ? (
                <span className="inline-flex items-center gap-1">
                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                  Loading assignments...
                </span>
              ) : (
                `${listCount} unassigned slot${listCount === 1 ? '' : 's'} need attention`
              )}
            </p>
            <Button variant="outline" size="sm" className="h-8 rounded-md" asChild>
              <Link
                to={withServiceInstanceId('/assignments?unassigned=true', serviceInstanceId)}
                onClick={() => setOpen(false)}
              >
                View all assignments
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
